import React, { useMemo, useState, useCallback } from "react";
import { Button, Col, Form, InputGroup, Modal, Row, Table } from "react-bootstrap";
import AsyncCreatableSelect from "react-select/async-creatable";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../../_helper/CurrencyContext";
import { useDisableScrollOnFocus } from "../../../../Hooks/useDisableScrollOnFocus";
import {
	formatAmount,
	formatDateList,
	PAYMENT_METHOD_OPTIONS,
} from "../../../PatientAccounts/utils/patientAccountUtils";
import { masterPharmaDoctorsAPI } from "../../../PharmaDoctors/PharmaDoctorService";
import {
	EMPTY_DOCTOR_FORM,
	buildDoctorSelectOption,
	hasDoctorFormInput,
	mapMasterDoctorToForm,
	sanitizeDoctorName,
} from "../utils/doctorFormUtils";
import { formatDate } from "../utils/dateUtils";
import {
	buildGeneralServiceOptions,
	buildInvestigationOptions,
	convertLineAmount,
	formatItemDisplayLabel,
	getPrescriptionBillingFlags,
} from "../utils/billingUtils";
import { toMoneyNumber } from "../../../../utils/financeEngine";
import "./BillingPaymentModal.scss";
import {
	BillingPharmacyMedicinesTable,
	BillingPharmacySummaryBreakdown,
	BillingPharmacyTaxBreakdown,
} from "./BillingPharmacySection";
import {
	BillingInvestigationsAdjustments,
	BillingInvestigationsSummaryBreakdown,
	BillingServicesAdjustments,
	BillingServicesSummaryBreakdown,
} from "./BillingServicesSection";
import {
	AmountInput,
	BillingAmountCell,
	BillingLineAmountInput,
	BillingLineQuantityInput,
	BillingPriorPaymentSummaryRows,
	BillingSectionDivider,
	CollapsibleSection,
	SourceBadge,
} from "./billingModalParts";

const buildNewDoctorOption = (name) => {
	const trimmed = sanitizeDoctorName(name);
	if (!trimmed) return null;
	return {
		value: `new:${trimmed}`,
		label: trimmed,
		isNew: true,
	};
};
const SessionStatusPill = ({ status }) => {
	const { t } = useTranslation();
	const normalized = String(status || "pending").toLowerCase();
	const isCompleted = normalized === "completed";
	return (
		<span
			className={`badge ${isCompleted ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"
				}`}
		>
			{isCompleted ? t("Completed") : t("Pending")}
		</span>
	);
};

const TreatmentPackageBillingPaymentModal = ({
	show,
	onHide,
	activePrescription,
	selectedPatient,
	treatmentDetails,
	treatmentPaymentHistory = [],
	packagePrice = 0,
	serviceItems,
	servicesBillingTotals,
	servicesDiscountType,
	servicesDiscountValue,
	servicesTaxConfig,
	onServicesDiscountChange,
	onServicesTaxChange,
	investigationsBillingTotals,
	investigationsDiscountType,
	investigationsDiscountValue,
	investigationsTaxConfig,
	onInvestigationsDiscountChange,
	onInvestigationsTaxChange,
	investigationItems,
	generalMasterList,
	labMasterList,
	cart = [],
	priorPharmacyItems = [],
	itemTaxes = {},
	totals = {},
	discountType = "Percentage",
	discountValue = 0,
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	showPosTax = true,
	billingSummary,
	billingBaseCurrency,
	defaultCurrency,
	convertCurrency,
	payment,
	onPaymentChange,
	errors,
	submitted,
	sectionOpen,
	onToggleSection,
	updateInvestigationAmount,
	updateInvestigationQuantity,
	updateServiceAmount,
	updateServiceQuantity,
	onRemoveInvestigation,
	onRemoveServiceItem,
	onAddServiceRow,
	onSelectServiceForRow,
	onAddInvestigationRow,
	onSelectInvestigationForRow,
	paymentBalance,
	onCollectPayment,
	onCollectAndMarkPaid,
	isSubmitting,
	isLoading,
	doctorId,
	doctorForm = {},
	onDoctorFormChange,
	onDoctorIdChange,
	selectedDoctorOption,
	onSelectedDoctorOptionChange,
	onClearDoctorSelection,
	onAddDoctorToMaster,
	addingDoctor = false,
}) => {
	const { t } = useTranslation();
	const { getCurrencySymbol, allowedCurrencies } = useCurrency();
	const disableScrollProps = useDisableScrollOnFocus();
	const [pharmacyBreakdownOpen, setPharmacyBreakdownOpen] = useState(true);
	const [servicesBreakdownOpen, setServicesBreakdownOpen] = useState(true);
	const [investigationsBreakdownOpen, setInvestigationsBreakdownOpen] = useState(true);

	const loadDoctorOptions = useCallback(async (inputValue) => {
		const search = inputValue?.trim();
		if (!search || search.length < 2) return [];

		try {
			const response = await masterPharmaDoctorsAPI({ search });
			const doctors = response?.data?.data || [];
			return doctors.map((doctor) => ({
				value: doctor._id,
				label: buildDoctorSelectOption(doctor).label,
				data: doctor,
			}));
		} catch (error) {
			console.error("Error searching doctors:", error);
			return [];
		}
	}, []);

	const doctorSelectStyles = useMemo(
		() => ({
			menuPortal: (base) => ({ ...base, zIndex: 10000 }),
			menu: (base) => ({ ...base, zIndex: 10000 }),
		}),
		[]
	);

	const handleDoctorSelect = (option) => {
		if (!option) {
			onClearDoctorSelection?.();
			return;
		}

		if (option.__isNew__ || option.isNew) {
			const name = sanitizeDoctorName(option.label || option.value);
			if (!name) {
				onClearDoctorSelection?.();
				return;
			}
			onDoctorIdChange?.(null);
			onSelectedDoctorOptionChange?.(buildNewDoctorOption(name));
			onDoctorFormChange?.({
				...EMPTY_DOCTOR_FORM,
				name,
			});
			return;
		}

		onSelectedDoctorOptionChange?.(option);
		onDoctorIdChange?.(option.value);
		onDoctorFormChange?.(mapMasterDoctorToForm(option.data));
	};

	const handleDoctorFieldChange = (field, value) => {
		if (doctorId && field !== "note") {
			onDoctorIdChange?.(null);
			onSelectedDoctorOptionChange?.(null);
		}
		onDoctorFormChange?.({ [field]: value });
		if (field === "name" && !doctorId) {
			onSelectedDoctorOptionChange?.(buildNewDoctorOption(value));
		}
	};

	const showAddDoctorButton = !doctorId && hasDoctorFormInput(doctorForm);
	const isDraftDoctor = !doctorId && Boolean(sanitizeDoctorName(doctorForm?.name));

	const prescription = activePrescription || {};
	const billingFlags = useMemo(
		() => getPrescriptionBillingFlags(prescription),
		[prescription]
	);
	const pharmacyCurrency = defaultCurrency || billingBaseCurrency || allowedCurrencies?.[0] || "INR";
	const billingCurrency = billingBaseCurrency || pharmacyCurrency;
	const paymentCurrency = payment.currency || billingCurrency;
	const billingCurrencySymbol = getCurrencySymbol(billingCurrency);

	const lineAmountInputProps = useMemo(
		() => ({
			displayCurrency: billingCurrency,
			convertCurrency,
			pharmacyCurrency,
			allowedCurrencies,
			getCurrencySymbol,
		}),
		[allowedCurrencies, billingCurrency, convertCurrency, getCurrencySymbol, pharmacyCurrency]
	);

	const generalServiceOptions = useMemo(
		() =>
			buildGeneralServiceOptions(
				generalMasterList,
				getCurrencySymbol,
				pharmacyCurrency,
				allowedCurrencies
			),
		[allowedCurrencies, generalMasterList, getCurrencySymbol, pharmacyCurrency]
	);

	const labInvestigationOptions = useMemo(
		() =>
			buildInvestigationOptions(
				labMasterList,
				getCurrencySymbol,
				pharmacyCurrency,
				allowedCurrencies
			),
		[allowedCurrencies, getCurrencySymbol, labMasterList, pharmacyCurrency]
	);

	const patientName =
		prescription?.patient?.name ||
		selectedPatient?.name ||
		selectedPatient?.full_name ||
		"-";
	const fileNumber =
		prescription?.patient?.health_id || selectedPatient?.health_id || "-";
	const prescriptionNumber = prescription?.appointment_invoice?.invoice_number || "-";
	const visitDate = formatDate(prescription?.createdAt);
	const sessions = treatmentDetails?.sessions || [];

	const usedServiceIds = new Set(
		serviceItems.filter((item) => item.service_id).map((item) => String(item.service_id))
	);
	const usedTestIds = new Set(
		investigationItems.filter((item) => item.test_id).map((item) => String(item.test_id))
	);

	const getServiceOptionsForRow = (item) => {
		if (!item.service_id) return generalServiceOptions;
		if (generalServiceOptions.some((opt) => String(opt.value) === String(item.service_id))) {
			return generalServiceOptions;
		}
		return [
			{
				value: item.service_id,
				label: formatItemDisplayLabel(
					item.service_name,
					item.amount,
					item.currency,
					getCurrencySymbol,
					pharmacyCurrency,
					allowedCurrencies
				),
			},
			...generalServiceOptions,
		];
	};

	const getInvestigationOptionsForRow = (item) => {
		if (!item.test_id) return labInvestigationOptions;
		if (labInvestigationOptions.some((opt) => String(opt.value) === String(item.test_id))) {
			return labInvestigationOptions;
		}
		return [
			{
				value: item.test_id,
				label: formatItemDisplayLabel(
					item.test_name,
					item.amount,
					item.currency,
					getCurrencySymbol,
					pharmacyCurrency,
					allowedCurrencies
				),
			},
			...labInvestigationOptions,
		];
	};

	// Include services discount/tax so the summary matches section adjustments.
	const additionalServicesTotal = toMoneyNumber(billingSummary.services_total ?? 0);
	const servicesSectionTotal = billingSummary.services_total ?? 0;
	const investigationsTotal = billingSummary.investigation_total ?? 0;
	const pharmacyGrandTotal = useMemo(() => {
		const value = totals?.netTotal ?? 0;
		if (billingCurrency === pharmacyCurrency) return toMoneyNumber(value);
		return toMoneyNumber(convertCurrency(value, pharmacyCurrency, billingCurrency));
	}, [billingCurrency, convertCurrency, pharmacyCurrency, totals?.netTotal]);

	const isReadOnlyService = (item) =>
		item.isConsultation || (item.isPrescribed && !item.isNewRow);
	const isSelectableService = (item) =>
		!item.isConsultation && (item.isNewRow || item.source === "added");
	const isReadOnlyInvestigation = (item) => item.isPrescribed && !item.isNewRow;
	const isSelectableInvestigation = (item) => item.isNewRow || item.source === "added";

	return (
		<Modal
			show={show}
			onHide={onHide}
			fullscreen="md-down"
			size="xl"
			scrollable
			centered
			backdrop="static"
			className="billing-payment-modal billing-payment-modal--treatment"
		>
			<Modal.Header closeButton className="billing-modal-header">
				<Modal.Title className="d-flex align-items-center gap-2 flex-wrap">
					{t("Billing & Payment")}
					<span className="badge billing-tp-type-badge">{t("Treatment Package")}</span>
				</Modal.Title>
			</Modal.Header>

			<Modal.Body className="billing-modal-body position-relative">
				{isLoading && (
					<div className="billing-modal-loader" aria-live="polite" aria-busy="true">
						<div className="billing-modal-loader-content">
							<i className="fa fa-spinner fa-spin billing-modal-loader-icon" />
							<span>{t("Loading billing details...")}</span>
						</div>
					</div>
				)}

				<div
					className={
						isLoading
							? "billing-modal-content billing-modal-content--loading"
							: "billing-modal-content"
					}
				>
					<div className="billing-patient-banner billing-patient-banner--compact">
						<div className="billing-patient-banner-head">
							<span className="billing-patient-avatar">
								<i className="fa fa-user-circle" />
							</span>
							<div className="billing-patient-head-main">
								<div className="billing-patient-name">{patientName}</div>
								<div className="billing-patient-meta">
									{fileNumber !== "-" && (
										<span>
											<i className="fa fa-id-card-o me-1" />
											{fileNumber}
										</span>
									)}
									{visitDate && visitDate !== "-" && (
										<span>
											<i className="fa fa-calendar me-1" />
											{visitDate}
										</span>
									)}
									{prescriptionNumber !== "-" && (
										<span className="billing-patient-chip" title={t("Prescription")}>
											<span className="billing-patient-chip-label">{t("Rx")}</span>
											{prescriptionNumber}
										</span>
									)}
								</div>
							</div>
						</div>
						<Row className="g-2 billing-doctor-row">
							<Col xs={12} lg={8}>
								<div className="billing-field-group">
									<div className="billing-field-label d-flex align-items-center justify-content-between gap-2">
										<span>{t("Prescribed Doctor")}</span>
										{doctorId ? (
											<span className="billing-doctor-linked-badge">{t("Linked")}</span>
										) : null}
									</div>
									<AsyncCreatableSelect
										key={selectedDoctorOption?.value || "billing-doctor-cleared"}
										inputId="billing-tp-doctor-search"
										classNamePrefix="react-select"
										cacheOptions
										isClearable
										allowCreateWhileLoading={false}
										loadOptions={loadDoctorOptions}
										value={selectedDoctorOption}
										onChange={handleDoctorSelect}
										placeholder={t("Search by name, email or phone...")}
										formatCreateLabel={(inputValue) =>
											`${t("Use")} "${inputValue.trim()}"`
										}
										isValidNewOption={(inputValue, _value, options) => {
											const search = String(inputValue || "").trim();
											if (search.length < 2) return false;
											const existing = (options || []).filter((opt) => !opt.__isNew__);
											return existing.length === 0;
										}}
										noOptionsMessage={({ inputValue }) =>
											!inputValue || inputValue.length < 2
												? t("Type at least 2 characters")
												: t("No doctors found — use the name above to continue")
										}
										menuPortalTarget={
											typeof document !== "undefined" ? document.body : undefined
										}
										menuPosition="fixed"
										menuShouldScrollIntoView
										styles={doctorSelectStyles}
									/>
									{isDraftDoctor ? (
										<p className="billing-doctor-draft-hint small text-muted mb-1 mt-1">
											{t("This doctor will be created automatically when you generate the bill.")}
										</p>
									) : null}
									{showAddDoctorButton ? (
										<Button
											variant="outline-primary"
											size="sm"
											type="button"
											className="mt-1"
											disabled={addingDoctor}
											onClick={() => onAddDoctorToMaster?.()}
										>
											{addingDoctor ? t("Adding...") : t("Add Doctor to Master")}
										</Button>
									) : null}
								</div>
							</Col>
							<Col xs={12} lg={4}>
								<div className="billing-field-group">
									<div className="billing-field-label">{t("Note")}</div>
									<Form.Control
										as="textarea"
										rows={1}
										size="sm"
										className="billing-note-input"
										value={doctorForm.note || ""}
										placeholder={t("Additional notes")}
										onChange={(e) => handleDoctorFieldChange("note", e.target.value)}
										onFocus={disableScrollProps?.onFocus}
										onBlur={disableScrollProps?.onBlur}
										onKeyDown={disableScrollProps?.onKeyDown}
									/>
								</div>
							</Col>
						</Row>
					</div>


					<Row className="billing-layout g-3">
						<Col lg={8}>
							<div className="billing-tp-details-card">
								<div className="billing-tp-details-grid">
									<div className="billing-tp-detail">
										<span className="billing-tp-detail-label">{t("Package Name")}</span>
										<span className="billing-tp-detail-value">
											{treatmentDetails?.packageName || "-"}
										</span>
									</div>
									<div className="billing-tp-detail">
										<span className="billing-tp-detail-label">{t("Package Price")}</span>
										<span className="billing-tp-detail-value">
											{formatAmount(packagePrice, billingCurrency, getCurrencySymbol)}
										</span>
									</div>
									<div className="billing-tp-detail">
										<span className="billing-tp-detail-label">{t("Session")}</span>
										<span className="billing-tp-detail-value">
											{treatmentDetails?.currentSession || "-"}
											{treatmentDetails?.noOfSessions
												? ` (${treatmentDetails.noOfSessions} ${t("sessions")})`
												: ""}
										</span>
									</div>
									<div className="billing-tp-detail">
										<span className="billing-tp-detail-label">{t("Doctor")}</span>
										<span className="billing-tp-detail-value">
											{prescription?.doctor?.name || "-"}
										</span>
									</div>
								</div>

								{sessions.length > 0 && (
									<div className="billing-tp-sessions">
										<button
											type="button"
											className="billing-tp-sessions-toggle"
											onClick={() => onToggleSection("sessions")}
											aria-expanded={sectionOpen.sessions}
										>
											<span>
												<i className="fa fa-list-ul me-1" aria-hidden="true" />
												{t("Sessions")}
												<span className="billing-tp-sessions-count">({sessions.length})</span>
											</span>
											<i
												className={`fa fa-chevron-${sectionOpen.sessions ? "up" : "down"}`}
												aria-hidden="true"
											/>
										</button>
										{sectionOpen.sessions && (
											<div className="billing-tp-sessions-body">
												<div className="table-responsive billing-table-wrap">
													<Table bordered size="sm" className="mb-0 billing-table">
														<thead>
															<tr>
																<th>#</th>
																<th>{t("Session")}</th>
																<th>{t("Duration")}</th>
																<th>{t("Schedule")}</th>
																<th className="text-center">{t("Status")}</th>
															</tr>
														</thead>
														<tbody>
															{sessions.map((session) => (
																<tr key={session.id}>
																	<td>{session.index}</td>
																	<td className="fw-semibold">{session.sessionName}</td>
																	<td>{session.duration}</td>
																	<td>{session.schedule}</td>
																	<td className="text-center">
																		<SessionStatusPill status={session.status} />
																	</td>
																</tr>
															))}
														</tbody>
													</Table>
												</div>
											</div>
										)}
									</div>
								)}
							</div>
							<div className="billing-sections-stack">
								{/* <CollapsibleSection
									variant="treatment"
									title={t("Treatment Package")}
									total={packagePrice}
									currency={billingCurrency}
									isOpen={sectionOpen.package}
									onToggle={() => onToggleSection("package")}
									getCurrencySymbol={getCurrencySymbol}
									itemCount={1}
								>
									<div className="billing-tp-package-line">
										<div className="billing-line-name">
											<span className="billing-line-title">
												{treatmentDetails?.packageName || t("Treatment Package")}
											</span>
											<SourceBadge variant="package">{t("Package")}</SourceBadge>
										</div>
										<div className="billing-tp-package-meta text-muted small">
											{treatmentDetails?.currentSession || "-"}
											{treatmentDetails?.noOfSessions
												? ` · ${treatmentDetails.noOfSessions} ${t("sessions")}`
												: ""}
										</div>
									</div>
								</CollapsibleSection> */}

								<CollapsibleSection
									variant="pharmacy"
									title={t("Pharmacy Medicines")}
									total={pharmacyGrandTotal}
									currency={billingCurrency}
									isOpen={sectionOpen.medicines}
									onToggle={() => onToggleSection("medicines")}
									getCurrencySymbol={getCurrencySymbol}
									itemCount={(priorPharmacyItems?.length || 0) + cart.length}
								>
									<div className="billing-table-wrap">
										<BillingPharmacyMedicinesTable
											t={t}
											cart={cart}
											priorPharmacyItems={priorPharmacyItems}
											itemTaxes={itemTaxes}
											totals={totals}
											posTaxType={posTaxType}
											pharmacyStateCode={pharmacyStateCode}
											patientStateCode={patientStateCode}
											displayCurrency={billingCurrency}
											pharmacyCurrency={pharmacyCurrency}
											convertCurrency={convertCurrency}
											getCurrencySymbol={getCurrencySymbol}
											showPosTax={showPosTax}
										/>
									</div>
									<BillingPharmacyTaxBreakdown
										t={t}
										cart={cart}
										itemTaxes={itemTaxes}
										totals={totals}
										posTaxType={posTaxType}
										pharmacyStateCode={pharmacyStateCode}
										patientStateCode={patientStateCode}
										displayCurrency={billingCurrency}
										pharmacyCurrency={pharmacyCurrency}
										convertCurrency={convertCurrency}
										getCurrencySymbol={getCurrencySymbol}
										showPosTax={showPosTax}
									/>
								</CollapsibleSection>

								<CollapsibleSection
									variant="services"
									title={t("Treatment & Other Services")}
									total={servicesSectionTotal}
									currency={billingCurrency}
									isOpen={sectionOpen.services}
									onToggle={() => onToggleSection("services")}
									getCurrencySymbol={getCurrencySymbol}
									itemCount={serviceItems.length}
								>
									<div className="table-responsive billing-table-wrap">
										<Table bordered size="sm" className="mb-2 billing-table services-table">
											<thead>
												<tr>
													<th scope="col">{t("Service")}</th>
													<th scope="col">{t("Quantity")}</th>
													<th scope="col" className="billing-col-amount">
														<span
															className="billing-col-amount-symbol"
															title={billingCurrency}
														>
															{billingCurrencySymbol}
														</span>
														<span className="billing-col-amount-label">{t("Amount")}</span>
													</th>
												</tr>
											</thead>
											<tbody>
												{serviceItems.length > 0 ? (
													serviceItems.map((item) => (
														<tr
															key={item.id}
															className={
																isSelectableService(item)
																	? "billing-row billing-row--editable"
																	: "billing-row billing-row--locked"
															}
														>
															<td>
																<div className="billing-line-name">
																	{isReadOnlyService(item) ? (
																		<>
																			<span className="billing-line-title">
																				{item.service_name}
																			</span>
																			{item.isConsultation && (
																				<SourceBadge variant="consultation">
																					{t("Consultation")}
																				</SourceBadge>
																			)}
																		{item.isPrescribed && !item.isConsultation && (
																			<SourceBadge variant="prescribed">
																				{t("Prescribed")}
																			</SourceBadge>
																		)}
																		{item.status && (
																			<SourceBadge
																				variant={
																					String(item.status).toLowerCase() ===
																					"completed"
																						? "completed"
																						: "pending"
																				}
																			>
																				{String(item.status).toLowerCase() ===
																				"completed"
																					? t("Completed")
																					: t("Pending")}
																			</SourceBadge>
																		)}
																		</>
																	) : isSelectableService(item) ? (
																		<Form.Select
																			size="sm"
																			value={item.service_id || ""}
																			onChange={(e) =>
																				onSelectServiceForRow(item.id, e.target.value)
																			}
																			{...disableScrollProps}
																		>
																			<option value="">{t("Select Service")}</option>
																			{getServiceOptionsForRow(item).map((opt) => (
																				<option
																					key={opt.value}
																					value={opt.value}
																					disabled={
																						usedServiceIds.has(String(opt.value)) &&
																						String(item.service_id) !== String(opt.value)
																					}
																				>
																					{opt.label}
																				</option>
																			))}
																		</Form.Select>
																	) : (
																		<span className="billing-line-title">
																			{item.service_name}
																		</span>
																	)}
																</div>
															</td>
															<td>
																<BillingLineQuantityInput
																	item={item}
																	updateFn={updateServiceQuantity}
																	disabled={
																		isSelectableService(item) && !item.service_id
																	}
																	disableScrollProps={disableScrollProps}
																/>
															</td>
															<td>
																<BillingAmountCell
																	onRemove={
																		isSelectableService(item)
																			? () => onRemoveServiceItem(item.id)
																			: null
																	}
																	removeLabel={t("Remove service")}
																>
																	<BillingLineAmountInput
																		item={item}
																		updateFn={updateServiceAmount}
																		disabled={
																			isSelectableService(item) && !item.service_id
																		}
																		{...lineAmountInputProps}
																		disableScrollProps={disableScrollProps}
																	/>
																</BillingAmountCell>
															</td>
														</tr>
													))
												) : (
													<tr>
														<td colSpan={3} className="text-center text-muted">
															{t("No services added")}
														</td>
													</tr>
												)}
											</tbody>
										</Table>
									</div>
								<Button
									variant="outline-primary"
									size="sm"
									className="billing-add-row-btn"
									onClick={onAddServiceRow}
								>
									<i className="fa fa-plus me-1" />
									{t("Add Service")}
								</Button>
								{serviceItems.length === 0 && (
									<div className="billing-tp-session-note" role="note">
										<i className="fa fa-info-circle me-1" aria-hidden="true" />
										{t(
											"Only pending session charges appear here. Completed services from earlier sessions are already included in the package invoice."
										)}
									</div>
								)}

								<BillingSectionDivider title={t("Discount & Tax")} />
									<BillingServicesAdjustments
										t={t}
										servicesBilling={servicesBillingTotals}
										servicesTaxConfig={servicesTaxConfig}
										servicesDiscountType={servicesDiscountType}
										servicesDiscountValue={servicesDiscountValue}
										onDiscountTypeChange={(type) =>
											onServicesDiscountChange({ discountType: type })
										}
										onDiscountValueChange={(value) =>
											onServicesDiscountChange({ discountValue: value })
										}
										onTaxConfigChange={onServicesTaxChange}
										displayCurrency={billingCurrency}
										getCurrencySymbol={getCurrencySymbol}
										posTaxType={posTaxType}
										pharmacyStateCode={pharmacyStateCode}
										patientStateCode={patientStateCode}
										disableScrollProps={disableScrollProps}
										showTax={billingFlags.showServicesTax}
									/>
								</CollapsibleSection>

								<CollapsibleSection
									variant="lab"
									title={t("Lab Investigations")}
									total={investigationsTotal}
									currency={billingCurrency}
									isOpen={sectionOpen.investigations}
									onToggle={() => onToggleSection("investigations")}
									getCurrencySymbol={getCurrencySymbol}
									itemCount={investigationItems.length}
								>
									<div className="table-responsive billing-table-wrap">
										<Table bordered size="sm" className="mb-2 billing-table investigations-table">
											<thead>
												<tr>
													<th scope="col">{t("Test Name")}</th>
													<th scope="col">{t("Quantity")}</th>
													<th scope="col" className="billing-col-amount">
														<span
															className="billing-col-amount-symbol"
															title={billingCurrency}
														>
															{billingCurrencySymbol}
														</span>
														<span className="billing-col-amount-label">{t("Amount")}</span>
													</th>
												</tr>
											</thead>
											<tbody>
												{investigationItems.length > 0 ? (
													investigationItems.map((item) => (
														<tr
															key={item.id}
															className={
																isSelectableInvestigation(item)
																	? "billing-row billing-row--editable"
																	: "billing-row billing-row--locked"
															}
														>
															<td>
																<div className="billing-line-name">
																	{isReadOnlyInvestigation(item) ? (
																		<>
																			<span className="billing-line-title">
																				{item.test_name}
																			</span>
																			<SourceBadge variant="prescribed">
																				{t("Prescribed")}
																			</SourceBadge>
																		</>
																	) : isSelectableInvestigation(item) ? (
																		<Form.Select
																			size="sm"
																			value={item.test_id || ""}
																			onChange={(e) =>
																				onSelectInvestigationForRow(
																					item.id,
																					e.target.value
																				)
																			}
																			{...disableScrollProps}
																		>
																			<option value="">{t("Select Investigation")}</option>
																			{getInvestigationOptionsForRow(item).map((opt) => (
																				<option
																					key={opt.value}
																					value={opt.value}
																					disabled={
																						usedTestIds.has(String(opt.value)) &&
																						String(item.test_id) !== String(opt.value)
																					}
																				>
																					{opt.label}
																				</option>
																			))}
																		</Form.Select>
																	) : (
																		<span className="billing-line-title">
																			{item.test_name}
																		</span>
																	)}
																</div>
															</td>
															<td>
																<BillingLineQuantityInput
																	item={item}
																	updateFn={updateInvestigationQuantity}
																	disabled={!item.test_id}
																	disableScrollProps={disableScrollProps}
																/>
															</td>
															<td>
																<BillingAmountCell
																	onRemove={() => onRemoveInvestigation(item.id)}
																	removeLabel={t("Remove investigation")}
																>
																	<BillingLineAmountInput
																		item={item}
																		updateFn={updateInvestigationAmount}
																		disabled={!item.test_id}
																		{...lineAmountInputProps}
																		disableScrollProps={disableScrollProps}
																	/>
																</BillingAmountCell>
															</td>
														</tr>
													))
												) : (
													<tr>
														<td colSpan={3} className="text-center text-muted">
															{t("No investigations added")}
														</td>
													</tr>
												)}
											</tbody>
										</Table>
									</div>
									<Button
										variant="outline-primary"
										size="sm"
										className="billing-add-row-btn"
										onClick={onAddInvestigationRow}
									>
										<i className="fa fa-plus me-1" />
										{t("Add Investigation")}
									</Button>

									<BillingSectionDivider title={t("Discount & Tax")} />
									<BillingInvestigationsAdjustments
										t={t}
										investigationsBilling={investigationsBillingTotals}
										investigationsTaxConfig={investigationsTaxConfig}
										investigationsDiscountType={investigationsDiscountType}
										investigationsDiscountValue={investigationsDiscountValue}
										onDiscountTypeChange={(type) =>
											onInvestigationsDiscountChange({ discountType: type })
										}
										onDiscountValueChange={(value) =>
											onInvestigationsDiscountChange({ discountValue: value })
										}
										onTaxConfigChange={onInvestigationsTaxChange}
										displayCurrency={billingCurrency}
										getCurrencySymbol={getCurrencySymbol}
										posTaxType={posTaxType}
										pharmacyStateCode={pharmacyStateCode}
										patientStateCode={patientStateCode}
										disableScrollProps={disableScrollProps}
										showTax={billingFlags.showLabTax}
									/>
								</CollapsibleSection>

								<CollapsibleSection
									variant="services"
									title={t("Treatment History")}
									total={0}
									currency={billingCurrency}
									isOpen={sectionOpen.history}
									onToggle={() => onToggleSection("history")}
									getCurrencySymbol={getCurrencySymbol}
									itemCount={treatmentPaymentHistory.length}
									hideTotal
								>
									<div className="table-responsive billing-table-wrap">
										<Table bordered size="sm" className="mb-0 billing-table">
											<thead>
												<tr>
													<th>{t("Date")}</th>
													<th>{t("Type")}</th>
													<th>{t("Description")}</th>
													<th className="text-end">{t("Paid Amount")}</th>
													<th>{t("Payment Method")}</th>
												</tr>
											</thead>
											<tbody>
												{treatmentPaymentHistory.length > 0 ? (
													treatmentPaymentHistory.map((entry, index) => (
														<tr key={index}>
															<td>{formatDateList(entry.date)}</td>
															<td>{entry.type}</td>
															<td className="text-muted" style={{ whiteSpace: "pre-line" }}>
																{entry.description}
															</td>
															<td className="text-end fw-semibold">
																{entry.is_payment
																	? formatAmount(
																			entry.paid_amount,
																			entry.currency,
																			getCurrencySymbol
																	  )
																	: ""}
															</td>
															<td style={{ textTransform: "capitalize" }}>
																{entry.is_payment ? entry.payment_mode || "-" : ""}
															</td>
														</tr>
													))
												) : (
													<tr>
														<td colSpan={3} className="text-center text-muted py-3">
															{t("No previous payments recorded.")}
														</td>
													</tr>
												)}
											</tbody>
										</Table>
									</div>
								</CollapsibleSection>
							</div>
						</Col>

						<Col lg={4}>
							<div className="billing-summary-sticky">
								<div className="billing-summary-card mb-3">
									<h6 className="billing-summary-card-title mb-3">{t("Billing Summary")}</h6>

									<div className="billing-tp-summary-rows">
										<div className="summary-row">
											<span>{t("Treatment Package")}</span>
											<strong>
												{formatAmount(packagePrice, billingCurrency, getCurrencySymbol)}
											</strong>
										</div>
										{additionalServicesTotal > 0 && (
											<div className="summary-row">
												<span>{t("Additional Services")}</span>
												<strong>
													{formatAmount(
														additionalServicesTotal,
														billingCurrency,
														getCurrencySymbol
													)}
												</strong>
											</div>
										)}
										{/* <div className="summary-row">
											<span>{t("Lab Investigations")}</span>
											<strong>
												{formatAmount(
													investigationsTotal,
													billingCurrency,
													getCurrencySymbol
												)}
											</strong>
										</div> */}
										<div className="summary-row">
											<span>{t("Package Sub Total")}</span>
											<strong>
												{formatAmount(
													billingSummary.package_sub_total ?? 0,
													billingCurrency,
													getCurrencySymbol
												)}
											</strong>
										</div>
									</div>

									<BillingPharmacySummaryBreakdown
										t={t}
										cart={cart}
										totals={totals}
										discountType={discountType}
										discountValue={discountValue}
										posTaxType={posTaxType}
										pharmacyStateCode={pharmacyStateCode}
										patientStateCode={patientStateCode}
										displayCurrency={billingCurrency}
										pharmacyCurrency={pharmacyCurrency}
										convertCurrency={convertCurrency}
										getCurrencySymbol={getCurrencySymbol}
										isOpen={pharmacyBreakdownOpen}
										onToggle={() => setPharmacyBreakdownOpen((prev) => !prev)}
										showPosTax={showPosTax}
									/>

									<BillingServicesSummaryBreakdown
										t={t}
										title={t("Treatment & Other Services")}
										grandTotalLabel={t("Services Total")}
										billingSummary={billingSummary}
										servicesTaxConfig={servicesTaxConfig}
										posTaxType={posTaxType}
										pharmacyStateCode={pharmacyStateCode}
										patientStateCode={patientStateCode}
										displayCurrency={billingCurrency}
										getCurrencySymbol={getCurrencySymbol}
										isOpen={servicesBreakdownOpen}
										onToggle={() => setServicesBreakdownOpen((prev) => !prev)}
										showTax={billingFlags.showServicesTax}
									/>

									<BillingInvestigationsSummaryBreakdown
										t={t}
										billingSummary={billingSummary}
										investigationsTaxConfig={investigationsTaxConfig}
										posTaxType={posTaxType}
										pharmacyStateCode={pharmacyStateCode}
										patientStateCode={patientStateCode}
										displayCurrency={billingCurrency}
										getCurrencySymbol={getCurrencySymbol}
										isOpen={investigationsBreakdownOpen}
										onToggle={() => setInvestigationsBreakdownOpen((prev) => !prev)}
										showTax={billingFlags.showLabTax}
									/>

									<hr className="billing-summary-divider" />
									<div className="billing-grand-summary-box">
										<div className="summary-row grand-total-row">
											<span>{t("Grand Total")}</span>
											<strong>
												{formatAmount(
													billingSummary.grand_total,
													billingCurrency,
													getCurrencySymbol
												)}
											</strong>
										</div>
										<BillingPriorPaymentSummaryRows
											t={t}
											paymentBalance={paymentBalance}
											displayCurrency={billingCurrency}
											getCurrencySymbol={getCurrencySymbol}
										/>
									</div>
								</div>

								<div className="billing-payment-card">
									<h6 className="billing-payment-title fw-bold mb-3">{t("Payment")}</h6>
									<Row className="g-2">
										<Col sm={6}>
											<Form.Group>
												<Form.Label>
													{t("Currency")} <span className="text-danger">*</span>
												</Form.Label>
												<Form.Select
													value={payment.currency}
													onChange={(e) => onPaymentChange({ currency: e.target.value })}
													isInvalid={submitted && !!errors.currency}
													{...disableScrollProps}
												>
													{(allowedCurrencies?.length
														? allowedCurrencies
														: [paymentCurrency]
													).map((code) => (
														<option key={code} value={code} title={code}>
															({getCurrencySymbol(code)}) {code}
														</option>
													))}
												</Form.Select>
												{submitted && errors.currency && (
													<div className="text-danger small mt-1">{errors.currency}</div>
												)}
											</Form.Group>
										</Col>
										<Col sm={6}>
											<Form.Group>
												<Form.Label>
													{t("Payment Method")} <span className="text-danger">*</span>
												</Form.Label>
												<Form.Select
													value={payment.payment_method}
													onChange={(e) =>
														onPaymentChange({ payment_method: e.target.value })
													}
													isInvalid={submitted && !!errors.payment_method}
													{...disableScrollProps}
												>
													{PAYMENT_METHOD_OPTIONS.map((option) => (
														<option key={option.value} value={option.value}>
															{t(option.label)}
														</option>
													))}
												</Form.Select>
												{submitted && errors.payment_method && (
													<div className="text-danger small mt-1">
														{errors.payment_method}
													</div>
												)}
											</Form.Group>
										</Col>
										<Col xs={12}>
											<Form.Group>
												<Form.Label>
													{paymentBalance.hasPriorPayment
														? t("Balance Due")
														: t("Grand Total")}
												</Form.Label>
												<div className="billing-grand-total-display">
													{formatAmount(
														paymentBalance.hasPriorPayment
															? paymentBalance.amountDue
															: paymentBalance.grandTotal,
														paymentCurrency,
														getCurrencySymbol
													)}
												</div>
												{paymentBalance.hasPriorPayment && (
													<small className="text-muted">
														{t("Invoice Total")}:{" "}
														{formatAmount(
															paymentBalance.grandTotal,
															paymentCurrency,
															getCurrencySymbol
														)}
														{" · "}
														{t("Paid")}:{" "}
														{formatAmount(
															paymentBalance.paidSoFar,
															paymentCurrency,
															getCurrencySymbol
														)}
													</small>
												)}
											</Form.Group>
										</Col>
										<Col xs={12}>
											<Form.Group>
												<Form.Label>
													{paymentBalance.hasPriorPayment
														? t("Amount to Collect")
														: t("Paid Amount")}{" "}
													<span className="text-danger">*</span>
												</Form.Label>
												<Form.Control
													type="text"
													value={payment.amount_received}
													onChange={(e) => {
														const value = e.target.value;
														if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
															onPaymentChange({ amount_received: value });
														}
													}}
													isInvalid={submitted && !!errors.amount_received}
													placeholder={t("Enter amount")}
													{...disableScrollProps}
												/>
												{submitted && errors.amount_received && (
													<div className="text-danger small mt-1">
														{errors.amount_received}
													</div>
												)}
											</Form.Group>
										</Col>
									</Row>

									<div className="billing-balance-box mt-2">
										<div
											className={`balance-row balance-row--status balance-row--${paymentBalance.status
												.toLowerCase()
												.replace(/[\s_]+/g, "-")}`}
										>
											<span>{t("Status")}</span>
											<strong>{t(paymentBalance.statusLabel)}</strong>
										</div>
										<div className="balance-row balance-row--pending">
											<span>
												{t("Balance")} ({paymentCurrency})
											</span>
											<strong>
												{formatAmount(
													paymentBalance.pending > 0 ? paymentBalance.pending : 0,
													paymentCurrency,
													getCurrencySymbol
												)}
											</strong>
										</div>
										{paymentBalance.showBaseCurrency && (
											<div className="balance-row balance-row--pending-alt">
												<span>
													{t("Balance")} ({billingBaseCurrency})
												</span>
												<strong>
													{formatAmount(
														paymentBalance.pendingBase > 0
															? paymentBalance.pendingBase
															: 0,
														billingBaseCurrency,
														getCurrencySymbol
													)}
												</strong>
											</div>
										)}
										{paymentBalance.change > 0 && (
											<div className="balance-row balance-row--change">
												<span>
													{t("Change Amount")} ({paymentCurrency})
												</span>
												<strong>
													{formatAmount(
														paymentBalance.change,
														paymentCurrency,
														getCurrencySymbol
													)}
												</strong>
											</div>
										)}
									</div>
								</div>
							</div>
						</Col>
					</Row>
				</div>
			</Modal.Body>

			<Modal.Footer className="billing-modal-footer flex-wrap gap-2">
				<Button
					variant="success"
					onClick={onCollectAndMarkPaid}
					disabled={isSubmitting || isLoading}
				>
					{t("Collect & Mark As Paid")}
				</Button>
				<Button variant="primary" onClick={onCollectPayment} disabled={isSubmitting || isLoading}>
					{t("Collect Payment")}
				</Button>
				<Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
					{t("Cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default TreatmentPackageBillingPaymentModal;
